import { AppwriteException } from 'appwrite';
import { isCommandError } from '../../services/appwrite/command-gateway';
import { reportClientError } from '../../services/appwrite/report-client-error';

/**
 * Los códigos vienen de dos lugares: `AppwriteException.type` (login,
 * registro, código) y `CommandError.code` (`resolve_account`,
 * `reset_password`, que sí pasan por `management-command`). Un solo mapa
 * alcanza porque Appwrite y el backend propio no repiten nombres.
 */
const MESSAGES: Record<string, string> = {
  account_not_found: 'No encontramos ninguna cuenta con ese usuario o email.',
  user_already_exists: 'Ya existe una cuenta con ese email.',
  user_invalid_credentials: 'El usuario o la contraseña no son correctos.',
  user_invalid_token: 'El código no es correcto o venció. Pide uno nuevo.',
  user_password_mismatch: 'Las contraseñas no coinciden.',
  password_recently_used: 'Ya usaste esa contraseña antes. Elige una distinta.',
  general_rate_limit_exceeded: 'Hiciste demasiados intentos. Espera un momento y vuelve a intentarlo.',
  validation_error: 'Revisa los datos: alguno no tiene el formato esperado.',
};

export function authErrorText(error: unknown): string {
  if (!error) return 'No se pudo completar la operación.';
  if (isCommandError(error)) return MESSAGES[error.code] ?? 'No se pudo completar la operación. Inténtalo nuevamente.';
  if (error instanceof AppwriteException && error.type) {
    return MESSAGES[error.type] ?? 'No se pudo completar la operación. Inténtalo nuevamente.';
  }
  return 'No se pudo completar la operación. Inténtalo nuevamente.';
}

/**
 * Si es uno de los códigos de `MESSAGES`, es un desenlace esperado (usuario o
 * contraseña incorrectos, email ya usado) que ya se explica con su propio
 * texto — no hace falta registrarlo como error del sistema. Lo que no está
 * en este mapa es lo inesperado: eso sí se reporta (ver `reportClientError`).
 */
export function isKnownAuthError(error: unknown): boolean {
  if (isCommandError(error)) return error.code in MESSAGES;
  if (error instanceof AppwriteException && error.type) return error.type in MESSAGES;
  return false;
}

/** Reporta solo lo inesperado — un desenlace ya cubierto por `MESSAGES` no es un error del sistema. */
export function reportUnknownAuthError(operation: string, error: unknown): void {
  if (isKnownAuthError(error)) return;
  reportClientError({ source: 'web-app', operation, message: authErrorText(error), details: String(error) });
}
